import { Box, Typography, InputLabel, Stack, InputAdornment, IconButton } from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider, frFR } from '@mui/x-date-pickers'
import dayjs, { Dayjs } from 'dayjs'
import { useState } from 'react'
import { datePickerSx } from './_styles'
import { TransmissionDatePickerProps } from './_props'
import { formatDayjsDDMMYYYY as formatDDMMYYYY, parseRawDDMMYYYYToDayjs } from '../../utils/date'
import { validateDate } from './_utils'
import ErrorIcon from '@mui/icons-material/Error'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import CloseIcon from '@mui/icons-material/Close'
import EventIcon from '@mui/icons-material/Event'

export default function TransmissionDatePicker({ question, initialValue, onAnswer, showInitialError }: TransmissionDatePickerProps) {
    const [selectedDate, setSelectedDate] = useState<Dayjs | null>(() => parseRawDDMMYYYYToDayjs(initialValue))
    const [errorMessage, setErrorMessage] = useState<string | undefined>(() =>
        showInitialError && question.required && !parseRawDDMMYYYYToDayjs(initialValue)
            ? 'Ce champ est obligatoire'
            : undefined,
    )
    const [open, setOpen] = useState(false)

    const handleChange = (date: Dayjs | null) => {
        setSelectedDate(date)

        if (!date) {
            onAnswer?.('')
            setErrorMessage(question.required ? 'Ce champ est obligatoire' : undefined)
            return
        }

        const validation = validateDate(date, {
            dateMin: question.dateMin,
            dateMax: question.dateMax,
        })

        if (!validation.isValid) {
            onAnswer?.('')
            setErrorMessage(validation.errorMessage)
            return
        }
        setErrorMessage(undefined)
        onAnswer?.(date.format('DD/MM/YYYY'))
    }

    const renderLabelIcon = () => {
        if (errorMessage) return <CancelIcon sx={[datePickerSx.dateIconActive, datePickerSx.errorIcon]} />
        if (selectedDate) return <CheckCircleIcon sx={datePickerSx.dateIconActive} />
        return null
    }

    return (
        <Box>
            <Box
                sx={{
                    maxWidth: {
                        lg: '24.25rem',
                    },
                }}
            >
                <Box sx={[datePickerSx.datePickerContainer, errorMessage ? datePickerSx.datePickerContainerError : {}]}>
                    <Box maxWidth={640} margin="0 auto">
                        <Stack sx={datePickerSx.dateLabelContainer}>
                            <InputLabel
                                htmlFor="date-picker-input"
                                sx={[
                                    datePickerSx.dateLabel,
                                    errorMessage
                                        ? datePickerSx.dateLabelError
                                        : selectedDate
                                        ? datePickerSx.dateLabelActive
                                        : datePickerSx.dateLabelInactive,
                                ]}
                            >
                                Date
                            </InputLabel>
                            {renderLabelIcon()}
                        </Stack>
                        <LocalizationProvider
                            dateAdapter={AdapterDayjs}
                            adapterLocale="fr"
                            localeText={frFR.components.MuiLocalizationProvider.defaultProps.localeText}
                        >
                            <DatePicker
                                value={selectedDate}
                                format="DD/MM/YYYY"
                                views={['year', 'month', 'day']}
                                onChange={handleChange}
                                open={open}
                                onOpen={() => setOpen(true)}
                                onClose={() => setOpen(false)}
                                slotProps={{
                                    popper: {
                                        sx: datePickerSx.popper,
                                    },
                                    textField: {
                                        InputProps: {
                                            endAdornment: (
                                                <InputAdornment position="end" sx={{ gap: '1rem' }}>
                                                    {selectedDate && selectedDate.toString().length > 0 && (
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => {
                                                                setSelectedDate(null)
                                                                setErrorMessage(
                                                                    question.required
                                                                        ? 'Ce champ est obligatoire'
                                                                        : undefined,
                                                                )
                                                                onAnswer?.('')
                                                            }}
                                                            edge="end"
                                                            sx={{ padding: 0 }}
                                                        >
                                                            <CloseIcon sx={{ fontSize: '1.25rem' }} />
                                                        </IconButton>
                                                    )}
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => setOpen(true)}
                                                        edge="end"
                                                        sx={{
                                                            padding: 0,
                                                        }}
                                                    >
                                                        <EventIcon sx={{ fontSize: '1.25rem' }} />
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        },
                                    },
                                }}
                            />
                            {!selectedDate && (
                                <Typography sx={{ display: 'none' }}>{dayjs().format('DD/MM/YYYY')}</Typography>
                            )}
                        </LocalizationProvider>
                    </Box>
                </Box>
                {errorMessage && (
                    <Stack sx={datePickerSx.errorMessageContainer}>
                        <ErrorIcon sx={datePickerSx.errorIcon} />
                        <Typography>{errorMessage}</Typography>
                    </Stack>
                )}
            </Box>
        </Box>
    )
}
